import React, { useState, useEffect } from 'react';
import { CalendarClock, ChevronRight, ChevronDown, CreditCard } from 'lucide-react';
import { colors, API, fmt, fmtNum, authHeaders } from '../utils/theme';
import { LoadingSpinner, EmptyState, Badge } from '../components/UI';

export default function AgendaVencimentosPage() {
  const [dias, setDias] = useState([]);
  const [semDia, setSemDia] = useState(0);
  const [loading, setLoading] = useState(true);
  const [diaSel, setDiaSel] = useState(null);          // dia selecionado (drill 1)
  const [titularExp, setTitularExp] = useState(null);  // titular expandido (drill 2)

  useEffect(() => {
    fetch(`${API}/cartoes/agenda-vencimentos`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setDias(d.dias || []); setSemDia(d.sem_dia || 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  const diaAtivo = dias.find(d => d.dia === diaSel);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Vencimentos por Dia</h2>
        <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
          Selecione um dia de vencimento → titular → cartões. Considera cartões ativos (exceto em negociação).
          {semDia > 0 && <span> · {semDia} cartão(ões) sem dia de vencimento cadastrado.</span>}
        </div>
      </div>

      {dias.length === 0 ? (
        <EmptyState message="Nenhum cartão com dia de vencimento cadastrado" icon={CalendarClock} />
      ) : (
        <>
          {/* Drill 1: grade de dias 1-31 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginBottom: 24 }}>
            {dias.map(d => {
              const ativo = d.dia === diaSel;
              return (
                <button key={d.dia}
                  onClick={() => { setDiaSel(ativo ? null : d.dia); setTitularExp(null); }}
                  style={{
                    textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '12px 14px',
                    background: ativo ? `${colors.primary}22` : colors.card,
                    border: `1px solid ${ativo ? colors.primary : colors.border}`,
                    transition: 'all 0.15s',
                  }}>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>Dia</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: ativo ? colors.primary : colors.text, lineHeight: 1 }}>{d.dia}</div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6 }}>
                    {d.qtd_cartoes} cartões · {d.qtd_titulares} titular(es)
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: colors.danger, marginTop: 2 }}>{fmt(d.total_fatura)}</div>
                </button>
              );
            })}
          </div>

          {/* Drill 2 + 3: titulares e cartões do dia selecionado */}
          {diaAtivo && (
            <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  Vencimentos do dia <span style={{ color: colors.primary }}>{diaAtivo.dia}</span>
                </div>
                <div style={{ fontSize: 13, color: colors.textMuted }}>
                  {diaAtivo.qtd_titulares} titulares · {diaAtivo.qtd_cartoes} cartões · <strong style={{ color: colors.danger }}>{fmt(diaAtivo.total_fatura)}</strong>
                </div>
              </div>

              {diaAtivo.titulares.map(t => {
                const exp = titularExp === t.id;
                return (
                  <div key={t.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    {/* Linha do titular (drill 2) */}
                    <div
                      onClick={() => setTitularExp(exp ? null : t.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', cursor: 'pointer',
                        background: exp ? `${colors.primary}10` : 'transparent',
                      }}>
                      {exp ? <ChevronDown size={16} color={colors.textMuted} /> : <ChevronRight size={16} color={colors.textMuted} />}
                      <span style={{ fontWeight: 600, flex: 1 }}>{t.nome}</span>
                      <Badge text={`${t.cartoes.length} cartões`} color={colors.info} />
                      <span style={{ fontWeight: 700, color: colors.danger, minWidth: 110, textAlign: 'right' }}>{fmt(t.total_fatura)}</span>
                    </div>

                    {/* Cartões do titular (drill 3) */}
                    {exp && (
                      <div style={{ padding: '0 18px 12px 44px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ color: colors.textMuted, fontSize: 11, textTransform: 'uppercase' }}>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Cartão</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Banco</th>
                              <th style={{ textAlign: 'center', padding: '6px 8px' }}>Final</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px' }}>Limite</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px' }}>Fatura</th>
                            </tr>
                          </thead>
                          <tbody>
                            {t.cartoes.map(c => (
                              <tr key={c.id} style={{ borderTop: `1px solid ${colors.border}55` }}>
                                <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                                  <CreditCard size={12} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom', color: colors.textMuted }} />
                                  {c.nome_cartao}
                                </td>
                                <td style={{ padding: '6px 8px', color: colors.textMuted }}>{c.banco_nome || '-'}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center', color: colors.textMuted }}>{c.numeracao_final || '-'}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(c.limite_total)}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: colors.danger }}>{fmt(c.fatura_atual)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!diaAtivo && (
            <div style={{ textAlign: 'center', color: colors.textMuted, fontSize: 13, padding: 20 }}>
              Clique em um dia acima para ver os titulares e cartões que vencem nele.
            </div>
          )}
        </>
      )}
    </div>
  );
}
